import type { FC, ReactNode } from 'react'

import { useEffect, useRef, useState } from 'react'
import { cn } from '@/lib/utils'

const GLYPHS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789#%&$@'

interface ScrambleTextProps {
    text: string
    className?: string
    duration?: number
    delay?: number
}

// Decodes its text from random glyphs, left to right, whenever the text
// changes. The server renders the final text, so nothing is hidden without
// JavaScript.
export const ScrambleText: FC<ScrambleTextProps> = ({ text, className, duration = 900, delay = 0 }): ReactNode => {
    const [display, setDisplay] = useState(text)
    const frame = useRef(0)

    useEffect(() => {
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            setDisplay(text)
            return
        }

        let start = 0

        const tick = (now: number) => {
            if (!start) start = now + delay

            const progress = Math.max(0, Math.min(1, (now - start) / duration))
            const revealed = Math.floor(progress * text.length)
            let output = ''

            for (let index = 0; index < text.length; index++) {
                const letter = text[index]

                if (letter === ' ' || index < revealed) output += letter
                else output += GLYPHS[Math.floor(Math.random() * GLYPHS.length)]
            }

            setDisplay(output)

            if (progress < 1) frame.current = requestAnimationFrame(tick)
        }

        frame.current = requestAnimationFrame(tick)

        return () => {
            cancelAnimationFrame(frame.current)
        }
    }, [text, duration, delay])

    return (
        <span className={cn('relative inline-block', className)}>
            <span className='sr-only'>
                {text}
            </span>
            <span aria-hidden>
                {display}
            </span>
        </span>
    )
}
